/**
 * Query Name Generator
 *
 * Generates meaningful tab names from SQL query content.
 * Examples:
 * - "SELECT * FROM ARH" → "ARH select"
 * - "SELECT * FROM ARD WHERE ID > 20" → "ARD select on ID"
 * - "SELECT * FROM A, B" → "A, B select"
 * - "CREATE TABLE Users" → "Users create"
 * - "DROP TABLE Products" → "Products drop"
 * - "UPDATE Orders SET x = 1" → "Orders update"
 */

/**
 * Query intent parsed from SQL
 */
export type QueryIntent =
  | { type: 'select'; tables: string[]; whereColumn?: string }
  | { type: 'insert'; table: string }
  | { type: 'update'; table: string; whereColumn?: string }
  | { type: 'delete'; table: string; whereColumn?: string }
  | { type: 'ddl'; action: string; objectType: string; objectName: string }
  | { type: 'use'; database: string }
  | { type: 'exec'; procedureName: string }
  | { type: 'unknown' };

/**
 * Remove SQL comments and string literals to avoid false positives.
 * Preserves bracketed identifiers but marks them for easy extraction.
 */
function stripCommentsAndStrings(sql: string): string {
  let result = sql;

  // Remove block comments /* ... */
  result = result.replace(/\/\*[\s\S]*?\*\//g, ' ');

  // Remove line comments -- ...
  result = result.replace(/--[^\n]*/g, ' ');

  // Remove single-quoted strings 'xxx'
  result = result.replace(/'(?:[^']|'')*'/g, "'_STR_'");

  // Remove double-quoted identifiers "xxx" - replace with content
  result = result.replace(/"((?:[^"]|"")*)"/g, (_, content) => content.replace(/""/g, '"'));

  // Keep bracketed identifiers [xxx] but remove escaped brackets
  // We keep the brackets for now as they are parsed in extractTableName
  result = result.replace(/\[([^\]]*(?:\]\][^\]]*)*)\]/g, (_, content) => {
    // Handle escaped brackets: ]] -> ]
    const unescaped = content.replace(/\]\]/g, ']');
    return `[${unescaped}]`;
  });

  return result;
}

/**
 * Extract table name from qualified name, removing schema/database prefix
 */
function extractTableName(qualifiedName: string): string {
  // Handle [schema].[table], schema.table, [table], etc.
  const parts = qualifiedName.split('.');
  const lastPart = parts[parts.length - 1];

  // Remove brackets if present
  return lastPart.replace(/^\[/, '').replace(/\]$/, '');
}

/**
 * Extract the first column name from a WHERE clause
 */
function extractFirstWhereColumn(sql: string, afterIndex: number): string | undefined {
  const afterWhere = sql.slice(afterIndex);

  // Match WHERE followed by column name (handles various formats)
  // Looks for: WHERE [optional schema.]column_name comparison_operator
  const match = afterWhere.match(
    /WHERE\s+(?:\[?[\w.]+\]?\s*\.\s*)?(\[?[\w]+\]?)\s*(?:=|<>|!=|>=?|<=?|LIKE|IN|IS|BETWEEN|NOT)/i
  );

  if (match && match[1]) {
    // Clean up the column name
    return match[1].replace(/^\[/, '').replace(/\]$/, '');
  }

  return undefined;
}

/**
 * Strip a leading "preamble" of statements that only set up context and
 * shouldn't drive the tab title. Scripts produced by SSMS (and hand-written
 * scripts) commonly start with `USE [db]`, batch separators (`GO`), and
 * `SET <option> ON/OFF` before the statement that actually matters. Removing
 * them lets the title reflect the real work (e.g. a CREATE OR ALTER below).
 *
 * `SET @variable = ...` is real logic, not a batch option, so it is preserved.
 */
function stripLeadingPreamble(normalized: string): string {
  let previous = '';
  let current = normalized.trim();

  while (current !== previous) {
    previous = current;
    current = current
      // USE <database>
      .replace(/^USE\s+(?:\[[^\]]+\]|[\w$]+)\s*;?\s*/i, '')
      // batch separator GO
      .replace(/^GO\b\s*;?\s*/i, '')
      // SET <option ...> ON|OFF (batch options, but not `SET @var = ...`)
      .replace(/^SET\s+(?!@)[^;@]*?\s+(?:ON|OFF)\b\s*;?\s*/i, '')
      // stray statement terminators
      .replace(/^;\s*/, '')
      .trim();
  }

  return current;
}

/**
 * Match a single (already normalized) SQL statement to a query intent.
 * Anchored to the start of the given text.
 */
function matchStatement(normalized: string): QueryIntent {
  // Check for USE database
  // Handle both [identifier] and plain identifier forms
  const useMatch = normalized.match(/^USE\s+(\[[^\]]+\]|[\w]+)/i);
  if (useMatch) {
    return {
      type: 'use',
      database: extractTableName(useMatch[1]),
    };
  }

  // Check for EXEC/EXECUTE procedure
  const execMatch = normalized.match(/^(?:EXEC(?:UTE)?)\s+(\[?[\w.]+\]?)/i);
  if (execMatch) {
    return {
      type: 'exec',
      procedureName: extractTableName(execMatch[1]),
    };
  }

  // Check for DDL statements. Handles combined dialect forms:
  // - MSSQL `CREATE OR ALTER`     -> 'create/alter'
  // - PG/MySQL/Oracle `CREATE OR REPLACE` -> 'create/replace'
  // Object name accepts multi-part bracketed identifiers, e.g. [dbo].[GetUsers].
  const ddlMatch = normalized.match(
    /^(CREATE\s+OR\s+ALTER|CREATE\s+OR\s+REPLACE|CREATE|ALTER|DROP)\s+(MATERIALIZED\s+VIEW|TABLE|VIEW|PROCEDURE|PROC|FUNCTION|INDEX|TRIGGER|DATABASE|SCHEMA|TYPE|SEQUENCE|SYNONYM)\s+(?:IF\s+(?:NOT\s+)?EXISTS\s+)?((?:\[[^\]]+\]|[\w$]+)(?:\.(?:\[[^\]]+\]|[\w$]+))*)/i
  );
  if (ddlMatch) {
    const rawAction = ddlMatch[1].replace(/\s+/g, ' ').toUpperCase();
    const action =
      rawAction === 'CREATE OR ALTER'
        ? 'create/alter'
        : rawAction === 'CREATE OR REPLACE'
          ? 'create/replace'
          : rawAction.toLowerCase();
    return {
      type: 'ddl',
      action,
      objectType: ddlMatch[2].replace(/\s+/g, ' ').toLowerCase(),
      objectName: extractTableName(ddlMatch[3]),
    };
  }

  // Check for TRUNCATE
  const truncateMatch = normalized.match(/^TRUNCATE\s+TABLE\s+(\[?[\w.]+\]?)/i);
  if (truncateMatch) {
    return {
      type: 'ddl',
      action: 'truncate',
      objectType: 'table',
      objectName: extractTableName(truncateMatch[1]),
    };
  }

  // Check for INSERT
  const insertMatch = normalized.match(/^INSERT\s+INTO\s+(\[?[\w.]+\]?)/i);
  if (insertMatch) {
    return {
      type: 'insert',
      table: extractTableName(insertMatch[1]),
    };
  }

  // Check for UPDATE
  // Handle: UPDATE table, UPDATE schema.table, UPDATE [schema].[table]
  const updateMatch = normalized.match(/^UPDATE\s+((?:\[[^\]]+\]|\w+)(?:\.(?:\[[^\]]+\]|\w+))*)/i);
  if (updateMatch) {
    const table = extractTableName(updateMatch[1]);
    const whereIndex = normalized.toUpperCase().indexOf('WHERE');
    const whereColumn = whereIndex > 0 ? extractFirstWhereColumn(normalized, whereIndex) : undefined;

    return {
      type: 'update',
      table,
      whereColumn,
    };
  }

  // Check for DELETE
  const deleteMatch = normalized.match(/^DELETE\s+(?:FROM\s+)?(\[?[\w.]+\]?)/i);
  if (deleteMatch) {
    const table = extractTableName(deleteMatch[1]);
    const whereIndex = normalized.toUpperCase().indexOf('WHERE');
    const whereColumn = whereIndex > 0 ? extractFirstWhereColumn(normalized, whereIndex) : undefined;

    return {
      type: 'delete',
      table,
      whereColumn,
    };
  }

  // Check for SELECT
  const selectMatch = normalized.match(/^(?:WITH\s+[\s\S]+?\)\s+)?SELECT/i);
  if (selectMatch) {
    const tables = extractTablesFromSelect(normalized);
    const whereIndex = normalized.toUpperCase().indexOf('WHERE');
    const whereColumn = whereIndex > 0 ? extractFirstWhereColumn(normalized, whereIndex) : undefined;

    return {
      type: 'select',
      tables,
      whereColumn,
    };
  }

  return { type: 'unknown' };
}

/**
 * Parse SQL query to extract intent (tables, type, etc.).
 *
 * A leading USE/GO/SET preamble is skipped so the title reflects the first
 * meaningful statement (e.g. `USE [db] GO CREATE OR ALTER PROC ...` titles from
 * the procedure, not the database). A script that is *only* a preamble (a bare
 * `USE [db]`) still yields the USE intent.
 */
export function parseQueryIntent(sql: string): QueryIntent {
  if (!sql || !sql.trim()) {
    return { type: 'unknown' };
  }

  const cleanSql = stripCommentsAndStrings(sql);
  const normalized = cleanSql.replace(/\s+/g, ' ').trim();

  // Prefer the first meaningful statement after any USE/GO/SET preamble.
  const body = stripLeadingPreamble(normalized);
  if (body && body !== normalized) {
    const bodyIntent = matchStatement(body);
    if (bodyIntent.type !== 'unknown') {
      return bodyIntent;
    }
  }

  // No preamble, or nothing meaningful after it (e.g. a bare USE): parse as-is.
  return matchStatement(normalized);
}

/**
 * Extract table names from a SELECT query
 */
function extractTablesFromSelect(sql: string): string[] {
  const tables: string[] = [];

  // Find FROM clause position
  const fromMatch = sql.match(/\bFROM\s+/i);
  if (!fromMatch || fromMatch.index === undefined) {
    return tables;
  }

  const afterFrom = sql.slice(fromMatch.index + fromMatch[0].length);

  // Find where FROM clause ends (at WHERE, GROUP BY, HAVING, ORDER BY, UNION, semicolon, or end)
  const endMatch = afterFrom.match(/\b(?:WHERE|GROUP\s+BY|HAVING|ORDER\s+BY|UNION|EXCEPT|INTERSECT|;|$)/i);
  const fromClause = endMatch && endMatch.index !== undefined
    ? afterFrom.slice(0, endMatch.index)
    : afterFrom;

  // Split by comma and JOIN to get individual table references
  // Handle: FROM A, B JOIN C ON ... LEFT JOIN D ON ...
  const tableRefs = fromClause
    .replace(/\b(?:INNER|LEFT|RIGHT|FULL|CROSS|OUTER)?\s*JOIN\b/gi, ',')
    .replace(/\bON\s+[^,]+/gi, '') // Remove ON conditions
    .split(',');

  for (const ref of tableRefs) {
    const trimmed = ref.trim();
    if (!trimmed) continue;

    // Extract table name (first identifier, before any alias)
    // Handles: schema.table alias, [schema].[table] AS alias, table
    const tableMatch = trimmed.match(/^(\[?[\w.]+\]?(?:\.\[?[\w]+\]?)?)/);
    if (tableMatch) {
      const tableName = extractTableName(tableMatch[1]);
      if (tableName && tableName !== '_ID_' && tableName !== '_STR_') {
        tables.push(tableName);
      }
    }
  }

  return tables;
}

/**
 * Title-case a DDL action for display, preserving the combined form.
 * 'create' -> 'Create', 'create/alter' -> 'Create/Alter'
 */
function formatDdlAction(action: string): string {
  return action
    .split('/')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('/');
}

/**
 * Generate a tab name from SQL query content
 */
export function generateQueryName(sql: string): string {
  const intent = parseQueryIntent(sql);

  switch (intent.type) {
    case 'select': {
      if (intent.tables.length === 0) {
        return 'Query';
      }
      const tablesStr = intent.tables.slice(0, 3).join(', ');
      const suffix = intent.tables.length > 3 ? '...' : '';
      const base = `${tablesStr}${suffix} select`;
      return intent.whereColumn ? `${base} on ${intent.whereColumn}` : base;
    }

    case 'insert':
      return `${intent.table} insert`;

    case 'update': {
      const base = `${intent.table} update`;
      return intent.whereColumn ? `${base} on ${intent.whereColumn}` : base;
    }

    case 'delete': {
      const base = `${intent.table} delete`;
      return intent.whereColumn ? `${base} on ${intent.whereColumn}` : base;
    }

    case 'ddl':
      return `${intent.objectName}(${formatDdlAction(intent.action)})`;

    case 'use':
      return `USE ${intent.database}`;

    case 'exec':
      return `${intent.procedureName} exec`;

    case 'unknown':
    default:
      return 'Query';
  }
}

/**
 * Extract base name from a potentially prefixed name.
 * "(1) Users select" -> "Users select"
 * "Users select" -> "Users select"
 */
export function extractBaseName(name: string): string {
  const match = name.match(/^\(\d+\)\s+(.+)$/);
  return match ? match[1] : name;
}

/**
 * Check if a name matches a base name (either directly or with prefix)
 */
function matchesBaseName(name: string, baseName: string): boolean {
  const extracted = extractBaseName(name);
  return extracted === baseName;
}

/**
 * Count how many existing names match the base name (including prefixed versions)
 */
function countMatchingNames(baseName: string, existingNames: string[]): number {
  return existingNames.filter((name) => matchesBaseName(name, baseName)).length;
}

/**
 * Deduplicate a name by adding numeric prefix if needed.
 * Returns the new name for this tab. If there are existing duplicates,
 * use renumberDuplicateNames to update all tabs with the same base name.
 */
export function deduplicateName(baseName: string, existingNames: string[]): string {
  const matchCount = countMatchingNames(baseName, existingNames);

  if (matchCount === 0) {
    // No duplicates, use plain name
    return baseName;
  }

  // There are existing names with this base - add next prefix number
  return `(${matchCount + 1}) ${baseName}`;
}

/**
 * Generate a map of tab IDs to their new prefixed names.
 * Call this after adding a new duplicate to renumber all tabs with the same base name.
 * Returns null if renumbering is not needed (0 or 1 matches).
 */
export function renumberDuplicateNames(
  baseName: string,
  tabs: Array<{ id: string; title: string; customTitle?: boolean }>
): Map<string, string> | null {
  // Find all tabs matching this base name (excluding custom-titled tabs)
  const matchingTabs = tabs.filter(
    (tab) => !tab.customTitle && matchesBaseName(tab.title, baseName)
  );

  // Only renumber if there are 2 or more matches
  if (matchingTabs.length < 2) {
    return null;
  }

  // Reverse to get chronological order (new tabs are prepended to array,
  // so oldest/first-opened is at the end)
  const chronologicalTabs = [...matchingTabs].reverse();

  const renameMap = new Map<string, string>();
  chronologicalTabs.forEach((tab, index) => {
    const newTitle = `(${index + 1}) ${baseName}`;
    if (tab.title !== newTitle) {
      renameMap.set(tab.id, newTitle);
    }
  });

  return renameMap.size > 0 ? renameMap : null;
}

/**
 * Find tabs that have a prefix but are now alone (no other tabs with same base name).
 * Returns a map of tab IDs to their unprefixed names.
 * Call this after removing a tab to clean up orphaned prefixes.
 */
export function cleanupOrphanedPrefixes(
  tabs: Array<{ id: string; title: string; customTitle?: boolean }>
): Map<string, string> | null {
  const renameMap = new Map<string, string>();

  // Group non-custom tabs by their base name
  const baseNameGroups = new Map<string, Array<{ id: string; title: string }>>();

  for (const tab of tabs) {
    if (tab.customTitle) continue;

    const baseName = extractBaseName(tab.title);
    const group = baseNameGroups.get(baseName) || [];
    group.push(tab);
    baseNameGroups.set(baseName, group);
  }

  // Find tabs that are alone but still have a prefix
  for (const [baseName, group] of baseNameGroups) {
    if (group.length === 1) {
      const tab = group[0];
      // Check if tab has a prefix (title !== baseName)
      if (tab.title !== baseName) {
        renameMap.set(tab.id, baseName);
      }
    }
  }

  return renameMap.size > 0 ? renameMap : null;
}

/**
 * Generate a unique tab name from SQL query content
 */
export function generateUniqueQueryName(sql: string, existingNames: string[]): string {
  const baseName = generateQueryName(sql);
  return deduplicateName(baseName, existingNames);
}
